import { BuiltInEdge, Edge, MarkerType, Node } from "@xyflow/react"
import { ensure } from "lopermut"
import { UseFormReturn } from "react-hook-form"
import { BuilderFormValues } from "./workflowsCanvas"
import { CARD_HEIGHT, CARD_SPACING } from "./constants"
import { layoutNodes } from "./repositionNodes"

const commonEdgeOptions: Partial<BuiltInEdge> = {
  type: "smoothstep",
  selectable: false,
  focusable: false,
  markerEnd: { type: MarkerType.Arrow, strokeWidth: 1, width: 20, height: 20 },
  pathOptions: { borderRadius: 14, offset: 5 },
}

const commonNodeOptions: Partial<Node> = {
  draggable: false,
}

type GetInitialNodesAndEdgesParams = {
  form: UseFormReturn<BuilderFormValues>
}

export function getInitialNodesAndEdges({ form }: GetInitialNodesAndEdgesParams) {
  const workflowNodes = form.getValues("nodes") ?? []
  const nodeDataMap = new Map(workflowNodes.map((n) => [n.id, n]))

  const nodeLevels = new Map<string, number>()
  const childrenMap = new Map<string, string[]>()

  // Initialize childrenMap for all campaign node IDs
  workflowNodes.forEach((n) => childrenMap.set(n.id, []))

  // Populate childrenMap based on parentId from workflowNodes
  // Root nodes (parentId: null) start at level 0
  workflowNodes.forEach((node) => {
    if (node.parentId && childrenMap.has(node.parentId)) {
      childrenMap.get(node.parentId)!.push(node.id)
    }
  })

  // Sort children by ID for consistent processing and layout
  childrenMap.forEach((children) => {
    children.sort((a, b) => a.localeCompare(b))
  })

  const queue: Array<{ nodeId: string; level: number }> = []

  // Seed with root nodes (no parentId)
  workflowNodes
    .filter((n) => !n.parentId)
    .forEach((node) => {
      nodeLevels.set(node.id, 0)
      queue.push({ nodeId: node.id, level: 0 })
    })

  // Breadth-first traversal to set levels for all dynamic nodes
  let head = 0
  while (head < queue.length) {
    const { nodeId: currentParentId, level: currentParentLevel } = ensure(queue[head++])
    const children = childrenMap.get(currentParentId) || []
    children.forEach((childId) => {
      if (nodeDataMap.has(childId) && !nodeLevels.has(childId)) {
        const childLevel = currentParentLevel + 1
        nodeLevels.set(childId, childLevel)
        queue.push({ nodeId: childId, level: childLevel })
      }
    })
  }

  // Transform workflowNodes (form nodes) into React Flow nodes with correct Y positions
  const flowNodes = workflowNodes
    .filter((node) => nodeLevels.has(node.id))
    .map((node) => {
      const level = nodeLevels.get(node.id)!
      return {
        id: node.id,
        type: node.type.toLowerCase(),
        data: node,
        position: {
          x: 0,
          y: level * (CARD_HEIGHT + CARD_SPACING),
        },
        ...commonNodeOptions,
      }
    })

  // Create edges for dynamic nodes
  const flowEdges: Edge[] = []
  workflowNodes
    .filter((node) => nodeLevels.has(node.id))
    .forEach((node) => {
      if (!node.parentId) return
      if (nodeDataMap.has(node.parentId) && nodeLevels.has(node.parentId)) {
        flowEdges.push({
          id: `edge-${node.parentId}-${node.id}`,
          source: node.parentId,
          sourceHandle:
            nodeDataMap.get(node.parentId)?.type === "Condition"
              ? (node.branch ?? "Pass")
              : "source",
          target: node.id,
          ...commonEdgeOptions,
        })
      }
    })

  return {
    nodes: layoutNodes(flowNodes, flowEdges),
    edges: flowEdges,
    commonNodeOptions,
    commonEdgeOptions,
  }
}
